import "./index.scss";

import { Component } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import classnames from "classnames";
import { Subject, fromEvent, takeUntil } from "rxjs";

import Icon from "common/core/icon";
import Tooltip from "common/core/tooltip";
import BinaryToggle from "common/form/inputs/binary_toggle";
import SROnly from "common/core/screen_reader";
import { isHybridTransactionType } from "common/mortgage/transactions/utils";
import { DOCUMENT_ICON } from "constants/icon";
import AlertMessage from "common/core/alert_message";
import { usePermissions } from "common/core/current_user_role";

import { DOCUMENT_PROPTYPE } from "../document_item_util";
import InstructionsList from "../instructions_list";
import DocumentRow from "./row";
import DocumentUploaderManagerHeader from "./header";
import DeleteDocumentModal from "../delete_document_modal";
import DeletedDocumentManager from "./deleted_document_manager";

const UP = Symbol("Up");
const DOWN = Symbol("Down");

const MESSAGES = defineMessages({
  documentDraggable: {
    id: "3966f5f6-66c4-4e5d-b0d9-af43c83e32fb",
    defaultMessage: "Document Draggable",
  },
  tableName: {
    id: "22fd0ad6-1f54-44d6-a32a-5adaab41a923",
    defaultMessage: "Uploaded Documents",
  },
  processingStarted: {
    id: "0e709a69-8afa-4815-a56f-d9c068f708f3",
    defaultMessage:
      'We are currently attempting to split and tag your title documents. You may place your order before your documents complete their processing. Our Closing Operations team will review all documents and adjust any files labeled "Additional Documents" that did not match to a template.',
  },
  processingDone: {
    id: "17636128-22b9-43ef-b17b-b7e68377c75c",
    defaultMessage:
      'We have finished our attempt to split and tag your title documents. Our Closing Operations team will review all documents and adjust the files labeled "Additional Documents" that did not match to a template.',
  },
});

const noop = () => {};

class DocumentUploaderManager extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dragDocumentId: null,
      dragTargetDocumentId: null,
      editingTitleDocumentId: null,
      editingTitleValue: null,
      confirmDeleteOpen: false,
      selectedDocument: null,
      /** Tells the managed components that we are currently deleting **/
      isDeleting: false,
      documentIdsSelectedForReordering: [],
      massSigningRequiresMeetingSelected: true,
      documentProcessingMessage: null,
    };

    this.unmounted$ = new Subject();
  }

  componentDidMount() {
    if (!this.props.hasPermissionFor("updateDocumentPosition")) {
      return;
    }

    fromEvent(window, "keydown").pipe(takeUntil(this.unmounted$)).subscribe(this.handleKeyDown);

    fromEvent(window, "keyup").pipe(takeUntil(this.unmounted$)).subscribe(this.handleKeyUp);
  }

  componentWillUnmount() {
    this.unmounted$.next();
    this.unmounted$.unsubscribe();
  }

  handleKeyDown = (e) => {
    if (e.key === "Escape") {
      this.setState({ documentIdsSelectedForReordering: [] });
    } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.view.event.preventDefault();

      const direction = e.key === "ArrowUp" ? UP : DOWN;

      if (e.altKey) {
        this.handleSendDocumentsToTopOrBottom(
          this.state.documentIdsSelectedForReordering,
          direction,
        );
      } else {
        this.handleMoveDocumentsUpOrDown(this.state.documentIdsSelectedForReordering, direction);
      }
    } else if (e.key === "Shift") {
      this.setState({ shiftPressed: true });
    }
  };

  handleKeyUp = (e) => {
    if (e.key === "Shift") {
      this.setState({ shiftPressed: false });
    }
  };

  handleClick = (documentId, event) => {
    if (!this.props.hasPermissionFor("updateDocumentPosition")) {
      return;
    }

    const { documents } = this.props;

    const document = documents.find((document) => document.id === documentId);

    if (document.isConsentForm) {
      return;
    }

    if (event.metaKey) {
      event.stopPropagation();
      event.preventDefault();

      if (event.altKey) {
        // Handle sending the selected documents to above or below the clicked document
        if (
          this.state.documentIdsSelectedForReordering.length === 0 ||
          (this.state.documentIdsSelectedForReordering.length === 1 &&
            this.state.documentIdsSelectedForReordering[0] === documentId)
        ) {
          return;
        }

        this.reorderDocumentsInCurrentBundleOrder(
          this.state.documentIdsSelectedForReordering,
          documentId,
          !event.shiftKey,
        );
      } else {
        // Handle selecting or unselecting a single document that is clicked
        const { documentIdsSelectedForReordering } = this.state;
        if (documentIdsSelectedForReordering.includes(documentId)) {
          this.setState({
            documentIdsSelectedForReordering: documentIdsSelectedForReordering.filter(
              (id) => id !== documentId,
            ),
          });
          return;
        }

        this.setState({
          documentIdsSelectedForReordering: [...documentIdsSelectedForReordering, documentId],
        });
      }
    } else if (event.altKey) {
      event.stopPropagation();
      event.preventDefault();

      // Handle sending a single document to the top or bottom
      if (event.shiftKey) {
        this.handleSendDocumentsToTopOrBottom([documentId], DOWN);
      } else {
        this.handleSendDocumentsToTopOrBottom([documentId], UP);
      }
    }
  };

  handleMoveDocumentsUpOrDown = (documentsToMove, direction) => {
    // First, we find the document right above the first selected document
    // This would be easier if we maintained the order of selecting when CTRL+Clicking to select, but not sure
    // if that's the desired effect
    const { documents } = this.props;

    if (!documentsToMove || documentsToMove.length === 0) {
      return;
    }

    let targetDocumentIndex =
      direction === UP
        ? Math.min(
            ...documentsToMove.map((documentToMove) =>
              documents.findIndex((document) => document.id === documentToMove),
            ),
          ) - 1
        : Math.max(
            ...documentsToMove.map((documentToMove) =>
              documents.findIndex((document) => document.id === documentToMove),
            ),
          ) + 1;

    targetDocumentIndex = Math.min(documents.length - 1, targetDocumentIndex);

    if (documents[targetDocumentIndex].isConsentForm) {
      this.handleSendDocumentsToTopOrBottom(documentsToMove, UP);
      return;
    }

    this.reorderDocumentsInCurrentBundleOrder(
      documentsToMove,
      documents[targetDocumentIndex].id,
      direction === UP,
    );
  };

  handleSendDocumentsToTopOrBottom = (documentIds, direction) => {
    const documentsList =
      direction === UP ? this.props.documents : [...this.props.documents].reverse();
    const targetDocumentId = documentsList.find((document) => !document.isConsentForm).id;

    if (documentIds.length === 1 && documentIds[0].id === targetDocumentId) {
      return;
    }

    this.reorderDocumentsInCurrentBundleOrder(documentIds, targetDocumentId, direction === UP);
  };

  reorderDocumentsInCurrentBundleOrder = (documentIds, targetDocumentId, above) => {
    const { documents, onReorderDocument } = this.props;

    const sortedDocumentIds = documentIds.sort(
      (a, b) =>
        documents.findIndex((document) => document.id === a) -
        documents.findIndex((document) => document.id === b),
    );

    onReorderDocument(sortedDocumentIds, targetDocumentId, above);
  };

  handleDragStart = (dragDocumentId) => {
    this.setState({ dragDocumentId });
  };

  handleDragOver = (dragTargetDocumentId) => {
    this.setState({ dragTargetDocumentId });
  };

  handleDragStop = () => {
    const { documents } = this.props;
    const { dragDocumentId, dragTargetDocumentId } = this.state;
    if (!dragDocumentId || !dragTargetDocumentId) {
      return;
    }

    const targetDocument = documents.find((document) => document.id === dragTargetDocumentId);

    this.setState(() => ({ dragTargetDocumentId: null, dragDocumentId: null }));

    if (targetDocument.isConsentForm) {
      this.handleSendDocumentsToTopOrBottom([dragDocumentId], UP);
    } else {
      this.props.onReorderDocument([dragDocumentId], dragTargetDocumentId);
    }
  };

  handleChangeDocumentTitle = ({ target: { value } }) => {
    this.setState(() => ({ editingTitleValue: value }));
  };

  handleStartRenameDocument = ({ id }) => {
    const { documents } = this.props;
    const { editingTitleDocumentId, editingTitleValue } = this.state;
    const { name } = documents.find((doc) => doc.id === id);
    const lastIndex = name.lastIndexOf(".") > 0 ? name.lastIndexOf(".") : name.length;
    const newEditingTitleValue = name.slice(0, lastIndex);
    this.setState(() => ({
      editingTitleDocumentId: id,
      editingTitleValue: newEditingTitleValue,
    }));

    // If we were already editing something when this new request for edit came in,
    // let's pass that update as well
    if (editingTitleDocumentId) {
      const oldEditingDocument = documents.find((doc) => doc.id === editingTitleDocumentId);
      this.optionallyCallRenameDocument(oldEditingDocument, editingTitleValue);
    }
  };

  handleStopRenameDocument = (document) => {
    const { editingTitleValue } = this.state;
    this.setState(() => ({ editingTitleDocumentId: null, editingTitleValue: null }));
    this.optionallyCallRenameDocument(document, editingTitleValue);
  };

  handleDocumentClick = (doc) => {
    this.props.openAnnotateModal(doc.id);
  };

  optionallyCallRenameDocument(document, editingTitleValue) {
    // We only want to alert the higher context with a valid title. Empty strings are not valid.
    if (editingTitleValue) {
      const { name } = document;
      const lastIndex = name.lastIndexOf(".");
      const fileExtension = lastIndex > 0 ? name.slice(lastIndex) : "";
      this.props.onRenameDocument(document, `${editingTitleValue}${fileExtension}`);
    }
  }

  generateToggleButtonHeader = (iconName) => {
    return (
      <div className="DocumentUploaderManager--ToggleButtonHeader">
        <Icon name={iconName} />
      </div>
    );
  };

  generateToggleButtonHeaderTip = (tipTarget, iconName) => {
    const { requiresNsaMeeting } = this.props;
    const tooltipCX = "DocumentUploaderManager--ToggleButtonHeaderTooltip";
    const cx = classnames(tooltipCX, { [`${tooltipCX}__hybrid`]: !requiresNsaMeeting });

    return (
      <div aria-hidden>
        <Tooltip target={tipTarget} placement="leftTop" className={cx}>
          <InstructionsList iconName={iconName} />
        </Tooltip>
      </div>
    );
  };

  showDeleteDocumentConfirm = (selectedDocument) => {
    this.setState({
      confirmDeleteOpen: true,
      selectedDocument,
    });
  };

  deleteDocument = () => {
    this.setState(
      {
        isDeleting: true,
      },
      () => {
        Promise.resolve(this.props.onDeleteDocuments(this.state.selectedDocument)).then(() => {
          this.clearDeleteConfirm();
        });
      },
    );
  };

  clearDeleteConfirm = () => {
    this.setState({ confirmDeleteOpen: false, selectedDocument: null, isDeleting: false });
  };

  toggleMassSigningRequiresMeetingSelected = () => {
    this.setState(
      (prevState) => ({
        massSigningRequiresMeetingSelected: !prevState.massSigningRequiresMeetingSelected,
      }),
      () => {
        this.props.onChangeDocumentProperties({
          signingRequiresMeeting: this.state.massSigningRequiresMeetingSelected,
          signerCanAnnotate: false,
          witnessRequired: false,
          notarizationRequired: false,
          proofingRequired: false,
        });
      },
    );
  };

  handleOnDocumentProcessingStarted = (documentId) => {
    const { showUploaderMessaging, onDocumentProcessingStarted, intl } = this.props;

    if (showUploaderMessaging) {
      this.setState({
        documentProcessingMessage: intl.formatMessage(MESSAGES.processingStarted),
      });
    }
    onDocumentProcessingStarted(documentId);
  };

  handleOnDocumentProcessingDone = (documentId) => {
    const { showUploaderMessaging, onDocumentProcessingDone, intl } = this.props;

    if (showUploaderMessaging) {
      this.setState({
        documentProcessingMessage: intl.formatMessage(MESSAGES.processingDone),
      });
    }
    onDocumentProcessingDone(documentId);
  };

  render() {
    const { confirmDeleteOpen, isDeleting, documentProcessingMessage } = this.state;
    const {
      viewer,
      onSelectDocument,
      selectedDocuments,
      transactionId,
      documents,
      onReorderDocument,
      onAddDocuments,
      onChangeDocumentProperties,
      onResubmitDocument,
      editRoute,
      canSetDocRequirements,
      canSetDocPermissions,
      canRequireMeeting,
      disableClick,
      isAnnotating,
      openAnnotateModal,
      onDocumentProcessingFailed,
      hideAnnotateButton,
      readOnly,
      allowDownload,
      canRequireEsign,
      showWitnessRequired,
      intl,
      requiresNsaMeeting,
      isMortgage,
      isTemplateEditing,
      transactionType,
      customerSigners,
      evaultEnabled,
      placeAnOrderEnabled,
      placeAnOrderLenderEnabled,
      esignAuthEnabled,
      organization,
      hasPermissionFor,
    } = this.props;

    const { canRequireVerificationOfFact } = organization;
    const canRequireProofing = this.props.canRequireProofing
      ? this.props.canRequireProofing
      : canRequireVerificationOfFact && !canRequireMeeting;

    const { dragTargetDocumentId, editingTitleDocumentId, editingTitleValue, shiftPressed } =
      this.state;

    const notarizationRequired = this.generateToggleButtonHeader(DOCUMENT_ICON.NOTARIZATION);
    const proofingRequired = this.generateToggleButtonHeader(DOCUMENT_ICON.PROOFING);
    const canFillInDocument = this.generateToggleButtonHeader(DOCUMENT_ICON.ANNOTATE);
    const witnessRequired = this.generateToggleButtonHeader(DOCUMENT_ICON.WITNESS);
    const signingMeetingRequired = this.generateToggleButtonHeader(DOCUMENT_ICON.IN_MEETING);
    const wetSignLineRequired = this.generateToggleButtonHeader(DOCUMENT_ICON.WET_SIGN_LINE);
    const isHybrid = isHybridTransactionType(transactionType);
    const templateDeleteDisabled = isTemplateEditing && documents.length < 2;
    const canManageDeletedDocuments = hasPermissionFor("manageDeletedDocuments");
    const canRequireWetSign = hasPermissionFor("requireWetSign");

    const managerClasses = classnames("DocumentUploaderManager", {
      DocumentUploaderManager__actionColumn: onReorderDocument,
      "DocumentUploaderManager__disable-highlight": shiftPressed,
    });

    const selectedDocumentName = this.state.selectedDocument
      ? this.state.selectedDocument.name
      : "";

    let requirementPermissionToggles;

    if (requiresNsaMeeting) {
      requirementPermissionToggles = (
        <>
          {canSetDocRequirements &&
            esignAuthEnabled &&
            this.generateToggleButtonHeaderTip(
              this.generateToggleButtonHeader(DOCUMENT_ICON.AUTH_ESIGN),
              DOCUMENT_ICON.AUTH_ESIGN,
            )}
          {canSetDocPermissions &&
            evaultEnabled &&
            this.generateToggleButtonHeaderTip(
              this.generateToggleButtonHeader(DOCUMENT_ICON.SECURE),
              DOCUMENT_ICON.SECURE,
            )}
          {canSetDocRequirements &&
            this.generateToggleButtonHeaderTip(notarizationRequired, DOCUMENT_ICON.NOTARIZATION)}
          {canSetDocRequirements &&
            canRequireEsign &&
            this.generateToggleButtonHeaderTip(wetSignLineRequired, DOCUMENT_ICON.WET_SIGN_LINE)}
          {canSetDocRequirements &&
            canRequireMeeting &&
            this.generateToggleButtonHeaderTip(signingMeetingRequired, DOCUMENT_ICON.IN_MEETING)}
          {canSetDocRequirements &&
            canRequireProofing &&
            this.generateToggleButtonHeaderTip(proofingRequired, DOCUMENT_ICON.PROOFING)}
          {canSetDocPermissions &&
            this.generateToggleButtonHeaderTip(canFillInDocument, DOCUMENT_ICON.ANNOTATE)}
          {canSetDocPermissions &&
            showWitnessRequired &&
            this.generateToggleButtonHeaderTip(witnessRequired, DOCUMENT_ICON.WITNESS)}
        </>
      );
    } else {
      requirementPermissionToggles = (
        <>
          {canSetDocRequirements && (
            <div className="DocumentUploaderManager--ToggleButtonHeader--Hybrid">
              <span id="wet-sign-toggle">
                <FormattedMessage
                  id="4eba6d86-c289-4c78-834d-326faf40ec4c"
                  defaultMessage="Wet Sign"
                />
              </span>
              {canRequireWetSign && (
                <div className="DocumentUploaderManager--ToggleAll--Hybrid">
                  <BinaryToggle
                    aria-labelledby="wet-sign-toggle"
                    onChange={this.toggleMassSigningRequiresMeetingSelected}
                    value={this.state.massSigningRequiresMeetingSelected}
                    automationId="all-hybrid"
                    size="small"
                  />
                </div>
              )}
              <Tooltip
                className="DocumentUploaderManager--ToggleButtonHeader--HybridTooltip"
                trigger="click"
                target={
                  <Icon
                    name="question"
                    className="DocumentUploaderManager--ToggleButtonHeader--HybridIcon"
                  />
                }
                placement="topRight"
                triggerButtonLabel="more details"
              >
                <FormattedMessage
                  id="61760e0e-3403-4c9a-a32b-fcaa6db78d7a"
                  defaultMessage="If turned on, signers are required to sign a physical copy of the document. If off, they are able to eSign."
                />
              </Tooltip>
            </div>
          )}
        </>
      );
    }

    return (
      <div className={managerClasses}>
        {confirmDeleteOpen && (
          <DeleteDocumentModal
            onCancel={this.clearDeleteConfirm}
            onDelete={this.deleteDocument}
            isDeleting={isDeleting}
            documentName={selectedDocumentName}
          />
        )}
        <DocumentUploaderManagerHeader
          selectedDocuments={selectedDocuments}
          documents={documents}
          onAddDocuments={onAddDocuments}
          isAnnotating={isAnnotating}
          hideAnnotateButton={hideAnnotateButton}
          requestOpenAnnotate={openAnnotateModal}
          canRequireProofing={canRequireProofing}
          hasEditColumn={!!editRoute}
          canSetDocRequirements={canSetDocRequirements}
          canSetDocPermissions={canSetDocPermissions}
          requiresNsaMeeting={requiresNsaMeeting}
          readOnly={readOnly}
        />
        <div className="DocumentUploaderManager--Wrapper">
          <ul aria-label={intl.formatMessage(MESSAGES.tableName)} role="table">
            <div role="rowgroup">
              <li role="row" key="header" className="DocumentUploaderManager--DocumentHeader">
                {onReorderDocument && (
                  <div
                    role="columnheader"
                    aria-label={intl.formatMessage(MESSAGES.documentDraggable)}
                    className="DocumentUploaderManager--DragCheckActions"
                  >
                    {onReorderDocument && <Icon name="drag-handle" />}
                  </div>
                )}
                <div className="DocumentUploaderManager--Row">
                  <div role="columnheader" className="DocumentUploaderManager--RowFilenameHeader">
                    <FormattedMessage
                      id="3377b301-608f-4365-90a5-708455af8201"
                      description="rowHeaderName"
                      defaultMessage="Name"
                    />
                  </div>
                  {onReorderDocument && (
                    <div
                      role="columnheader"
                      className="DocumentUploaderManager--RowFieldsPlacedHeader"
                    >
                      <FormattedMessage
                        id="1295e8f3-0065-46f4-b6d1-40ce8849a04b"
                        defaultMessage="Fields Placed"
                      />
                    </div>
                  )}
                </div>
                <div className="DocumentUploaderManager--ToggleButtonContainer">
                  <div role="columnheader">
                    <SROnly>
                      <FormattedMessage
                        id="dce3c29b-64df-4f6f-aca6-8192d1a90f8e"
                        defaultMessage="Document Requirements"
                      />
                    </SROnly>
                  </div>
                  {requirementPermissionToggles}
                  {editRoute && <div className="DocumentUploaderManager--EditLinkHeader" />}
                </div>
              </li>
            </div>
            {documentProcessingMessage && (
              <AlertMessage className="DocumentUploaderManager--AlertMessage" kind="info">
                {documentProcessingMessage}
              </AlertMessage>
            )}
            <div role="rowgroup">
              {documents.map((document) => (
                <DocumentRow
                  key={document.id}
                  document={document}
                  customerSigners={customerSigners}
                  isHybrid={isHybrid}
                  isMortgage={isMortgage}
                  isTarget={document.id === dragTargetDocumentId}
                  isSelected={Boolean(selectedDocuments[document.id])}
                  workingTitle={document.id === editingTitleDocumentId ? editingTitleValue : null}
                  onSelectToggle={onSelectDocument}
                  onDeleteDocument={this.showDeleteDocumentConfirm}
                  onStopRenameDocument={this.handleStopRenameDocument}
                  onResubmitDocument={onResubmitDocument}
                  viewer={viewer}
                  onStartRenameDocument={this.handleStartRenameDocument}
                  onChangeDocumentTitle={this.handleChangeDocumentTitle}
                  onChangeProperty={onChangeDocumentProperties}
                  onDocumentClick={() => this.handleDocumentClick(document)}
                  onClick={this.handleClick}
                  disableClick={disableClick}
                  onDragStart={this.handleDragStart}
                  onDragStop={this.handleDragStop}
                  onDragOver={this.handleDragOver}
                  canRequireProofing={canRequireProofing}
                  disableDragging={!onReorderDocument}
                  editRoute={editRoute}
                  evaultEnabled={evaultEnabled}
                  placeAnOrderEnabled={placeAnOrderEnabled}
                  placeAnOrderLenderEnabled={placeAnOrderLenderEnabled}
                  canSetDocRequirements={canSetDocRequirements}
                  canSetDocPermissions={canSetDocPermissions}
                  canRequireMeeting={canRequireMeeting}
                  disableSignerCanAnnotate={requiresNsaMeeting}
                  showWitnessRequired={showWitnessRequired}
                  canRequireEsign={canRequireEsign}
                  onDocumentProcessingStarted={this.handleOnDocumentProcessingStarted}
                  onDocumentProcessingDone={this.handleOnDocumentProcessingDone}
                  onDocumentProcessingFailed={onDocumentProcessingFailed}
                  requiresNsaMeeting={requiresNsaMeeting}
                  allowDownload={allowDownload}
                  selectedForReordering={this.state.documentIdsSelectedForReordering.includes(
                    document.id,
                  )}
                  templateDeleteDisabled={templateDeleteDisabled}
                  esignAuthEnabled={esignAuthEnabled}
                />
              ))}
            </div>
          </ul>
          {onReorderDocument && (
            <div>
              <div className="DocumentUploaderManager--TotalDocuments">
                <FormattedMessage
                  id="3009a3b6-0496-4d70-bd00-afae64056196"
                  defaultMessage="Total Documents: {totalDocuments}"
                  values={{ totalDocuments: documents.length }}
                />
              </div>
            </div>
          )}
          {canManageDeletedDocuments && <DeletedDocumentManager transactionId={transactionId} />}
        </div>
      </div>
    );
  }
}

DocumentUploaderManager.propTypes = {
  /** Called with the document ID being dragged and the ID of document at the desired, new index */
  onReorderDocument: PropTypes.func,
  /** Called with document object selected */
  onSelectDocument: PropTypes.func,
  /** Called with document object renamed and the new title */
  onRenameDocument: PropTypes.func.isRequired,
  /** Called with the document to be deleted when a single document delete, otherwise mass delete */
  onResubmitDocument: PropTypes.func.isRequired,
  /** Called with document object to reupload a document to a transaction */
  onDeleteDocuments: PropTypes.func.isRequired,
  /**
   * Called with an object of the desired props and optionally for a single document,
   * the applicable document. If this is `undefined`, its assumed a mass (selected) edit.
   */
  onChangeDocumentProperties: PropTypes.func.isRequired,
  openAnnotateModal: PropTypes.func,
  onAddDocuments: PropTypes.func,
  /** A map of documentId (string) to bool if selected */
  selectedDocuments: PropTypes.objectOf(PropTypes.bool).isRequired,
  documents: PropTypes.arrayOf(DOCUMENT_PROPTYPE).isRequired,
  transactionId: PropTypes.string,
  viewer: PropTypes.shape({
    user: PropTypes.shape({
      organizationMembership: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  }),
  canRequireEsign: PropTypes.bool,
  showWitnessRequired: PropTypes.bool,
  /** Provides checkboxes allowing user to modify multiple documents at once. **/
  canSetDocRequirements: PropTypes.bool,
  canSetDocPermissions: PropTypes.bool,
  evaultEnabled: PropTypes.bool,
  placeAnOrderEnabled: PropTypes.bool,
  placeAnOrderLenderEnabled: PropTypes.bool,
  /** Function for generating a edit link given an id key **/
  editRoute: PropTypes.func,
  canRequireMeeting: PropTypes.bool,
  /** Prevent document names from being clickable */
  disableClick: PropTypes.bool,
  /** Tells the managed components that we are currently annotating **/
  isAnnotating: PropTypes.bool,
  /** Optional callbacks when DocumentStatePoller status changes.
   *  Used to notify a higher component that the document is ready.
   */
  onDocumentProcessingStarted: PropTypes.func,
  onDocumentProcessingDone: PropTypes.func,
  onDocumentProcessingFailed: PropTypes.func,
  hideAnnotateButton: PropTypes.bool,
  showUploaderMessaging: PropTypes.bool,
  readOnly: PropTypes.bool,
  allowDownload: PropTypes.bool,
  requiresNsaMeeting: PropTypes.bool,
  isMortgage: PropTypes.bool,
  isTemplateEditing: PropTypes.bool,
  transactionType: PropTypes.string,
  customerSigners: PropTypes.arrayOf(PropTypes.object),
  esignAuthEnabled: PropTypes.bool,
};

DocumentUploaderManager.defaultProps = {
  canSetDocRequirements: true,
  canSetDocPermissions: true,
  canRequireMeeting: false,
  canRequireEsign: false,
  showWitnessRequired: true,
  disableClick: false,
  isAnnotating: false,
  requiresNsaMeeting: true,
  isMortgage: false,
  isTemplateEditing: false,
  showUploaderMessaging: false,
  onDocumentProcessingStarted: noop,
  onDocumentProcessingDone: noop,
};

export default (props) => {
  const { hasPermissionFor } = usePermissions();

  return (
    <DocumentUploaderManager {...props} intl={useIntl()} hasPermissionFor={hasPermissionFor} />
  );
};
